import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { SUPPRESS_ERROR_TOAST } from '@core/http/http-context-tokens';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { PagedPatientsDto, PatientDto } from '../../dtos/patient.dto';
import { PatientMetricsDto } from '../../dtos/patient-metrics.dto';
import { ClientRefDto, PagedClientRefsDto } from '../../dtos/client-ref.dto';
import {
  ConsultationAttachmentDto, ConsultationDischargeDto, ConsultationSectionDtoMap,
  PagedConsultationSummariesDto, PatientDewormingDto, PatientVaccinationDto,
} from '../../dtos/clinical-history.dto';
import { ConsultationSectionKey } from '../../../domain/models/clinical-history.model';
import { PagedInvoicesDto } from '../../dtos/invoice.dto';
import { DepartmentTransferResultDto } from '@features/shared/departments/data/dtos/department-transfer-result.dto';

@Injectable()
export class PatientsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Observable<PagedPatientsDto> {
    return this.http.get<PagedPatientsDto>(`${this.apiUrl}${API_ENDPOINTS.patients.list}`, {
      params: this.buildParams({ page, pageSize }, sortBy, sortDir, filters, departmentId),
    });
  }

  public search(
    term: string,
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Observable<PagedPatientsDto> {
    return this.http.get<PagedPatientsDto>(`${this.apiUrl}${API_ENDPOINTS.patients.search}`, {
      params: this.buildParams({ term, page, pageSize }, sortBy, sortDir, filters, departmentId),
    });
  }

  public getById(id: string): Observable<PatientDto> {
    return this.http.get<PatientDto>(`${this.apiUrl}${API_ENDPOINTS.patients.byId(id)}`);
  }

  public getMetrics(departmentId?: number, archived?: 'true' | 'false'): Observable<PatientMetricsDto> {
    const params: Record<string, string | number> = {};
    if (departmentId !== undefined) params['departmentId'] = departmentId;
    if (archived !== undefined) params['filters[archived]'] = archived;
    return this.http.get<PatientMetricsDto>(`${this.apiUrl}${API_ENDPOINTS.patients.metrics}`, { params });
  }

  public create(payload: Record<string, unknown>, departmentId?: number): Observable<PatientDto> {
    return this.http.post<PatientDto>(`${this.apiUrl}${API_ENDPOINTS.patients.list}`, payload, {
      params: departmentId === undefined ? {} : { departmentId },
    });
  }

  public update(id: string, payload: Record<string, unknown>): Observable<PatientDto> {
    return this.http.patch<PatientDto>(`${this.apiUrl}${API_ENDPOINTS.patients.byId(id)}`, payload);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.patients.byId(id)}`);
  }

  public moveToDepartment(id: string, departmentId: number): Observable<DepartmentTransferResultDto> {
    return this.http.post<DepartmentTransferResultDto>(
      `${this.apiUrl}${API_ENDPOINTS.patients.department(id)}`, { DepartmentId: departmentId },
    );
  }

  // Hits the generic `POST /api/patients/{id}/weight` passthrough (PatientsController.
  // CreateResource) with ProVet's own field names -- unlike the typed patient endpoints above,
  // this one forwards the JSON body to ProVet unmodified, so building the raw `weight`/`timestamp`
  // shape (instead of PascalCase) belongs here rather than in the domain repository.
  public createWeightRecord(id: string, weightKg: number, recordedAt: Date): Observable<unknown> {
    return this.http.post(`${this.apiUrl}${API_ENDPOINTS.patients.weight(id)}`, {
      weight: weightKg,
      timestamp: recordedAt.toISOString(),
    });
  }

  public uploadPicture(id: string, file: File): Observable<PatientDto> {
    const formData = new FormData();
    formData.append('picture', file);

    return this.http.patch<PatientDto>(`${this.apiUrl}${API_ENDPOINTS.patients.picture(id)}`, formData);
  }

  // Plain HTTP calls against the clients endpoint (same API_URL base clients' own datasource
  // uses) to resolve tutor display names -- NOT an import from `@features/web/clients/...`, to
  // keep feature isolation intact.
  public getTutor(id: string): Observable<ClientRefDto> {
    return this.http.get<ClientRefDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`);
  }

  // Batched variant of getTutor -- resolves multiple tutors in a single request instead of one
  // HTTP call per tutor id (mirrors AppointmentClientLookupRemoteDatasource.getByIds against the
  // same GET /api/clients/by-ids endpoint).
  public getTutorsByIds(ids: readonly string[]): Observable<PagedClientRefsDto> {
    return this.http.get<PagedClientRefsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byIds}`, {
      params: { ids: ids.join(',') },
    });
  }

  // `term` may be empty -- the backend treats a blank term as unfiltered, so an empty term returns
  // the clinic's full tutor list rather than nothing. `sortBy`/`sortDir` match the clients list
  // page's name column so this dropdown shows tutors alphabetically by default (mirrors the
  // appointments feature's AppointmentClientLookupRemoteDatasource.search).
  public searchTutors(term: string): Observable<PagedClientRefsDto> {
    return this.http.get<PagedClientRefsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.search}`, {
      params: { term, page: 1, pageSize: 20, sortBy: 'firstName', sortDir: 'asc' },
    });
  }

  // `getVaccinations`/`getDewormings` return a bare JSON array (`IReadOnlyCollection<T>` on the
  // backend), NOT a `PagedDto` envelope -- no `page`/`pageSize` params are sent, mirroring the
  // backend's own defaults (page = 1, pageSize = 100).
  public getVaccinations(patientId: string): Observable<readonly PatientVaccinationDto[]> {
    return this.http.get<readonly PatientVaccinationDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.patients.vaccinations(patientId)}`,
    );
  }

  public getDewormings(patientId: string): Observable<readonly PatientDewormingDto[]> {
    return this.http.get<readonly PatientDewormingDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.patients.dewormings(patientId)}`,
    );
  }

  // `GET /api/patients/{patientId}/consultations/summary` -- the only paginated clinical endpoint
  // of this datasource: `page`/`pageSize` are real, forwarded to ProVet as-is, and the response is
  // a `PagedDto<ConsultationSummaryDto>` envelope the repository unwraps.
  public getConsultationSummaries(
    patientId: string, organizationId: number, page: number, pageSize: number,
  ): Observable<PagedConsultationSummariesDto> {
    return this.http.get<PagedConsultationSummariesDto>(
      `${this.apiUrl}${API_ENDPOINTS.patients.consultationsSummary(patientId)}`,
      { params: { organizationId, page, pageSize } },
    );
  }

  // One request per opened tab of the consultation detail page. The `section` literal both picks
  // the route and resolves the response type through `ConsultationSectionDtoMap`, so callers keep
  // full typing off one method.
  public getConsultationSection<K extends ConsultationSectionKey>(
    patientId: string, consultationId: string, section: K,
  ): Observable<ConsultationSectionDtoMap[K]> {
    return this.http.get<ConsultationSectionDtoMap[K]>(
      `${this.apiUrl}${API_ENDPOINTS.patients.consultationSection(patientId, consultationId, section)}`,
    );
  }

  // `POST .../discharge` -- appends the AI report to the consultation as its discharge
  // instructions and answers with the refreshed `discharge` section, so the caller can patch the
  // "Alta" tab without a second GET. `Html` is PascalCase like every other typed body of this
  // datasource (`moveToDepartment`'s `DepartmentId`); the raw ProVet passthroughs are the
  // exception, not this. The error toast is suppressed because the modal shows its own, including
  // the dedicated message for the 403 the backend answers while its feature key is off.
  public saveConsultationDischarge(
    patientId: string, consultationId: string, html: string,
  ): Observable<ConsultationDischargeDto> {
    return this.http.post<ConsultationDischargeDto>(
      `${this.apiUrl}${API_ENDPOINTS.patients.consultationDischarge(patientId, consultationId)}`,
      { Html: html },
      { context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true) },
    );
  }

  // Standalone endpoint, not part of `ConsultationSectionDtoMap`: resolves the lab report(s)
  // attached to one lab request, called lazily when its drawer opens (not part of the eager
  // `analysis` section fetch, since it costs its own ProVet round-trip per referral).
  public getConsultationAnalysisReferralAttachments(
    patientId: string, consultationId: string, referralId: string,
  ): Observable<readonly ConsultationAttachmentDto[]> {
    return this.http.get<readonly ConsultationAttachmentDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.patients.consultationAnalysisReferralAttachments(patientId, consultationId, referralId)}`,
    );
  }

  // `GET /api/patients/{patientId}/invoices` (PatientsController.cs) returns a paginated
  // `PagedDto<InvoiceDto>` envelope (see BillingDtos.cs/PagedDto.cs), already narrowed
  // server-side to invoices whose `PatientIds` include this patient -- `page`/`pageSize` are
  // forwarded as-is, same as the clients feature's own `getInvoices(clientId, page, pageSize)`.
  // See `BillingQueryService.GetInvoicesForPatientAsync`'s own doc-comment for a known
  // pre-existing backend nuance: that patient-scoped filter is applied AFTER pagination, so a
  // requested page beyond the first may not be 100% reliable if the owning client has more than
  // 100 invoices of their own -- out of scope for this frontend wiring.
  public getInvoices(patientId: string, page: number, pageSize: number): Observable<PagedInvoicesDto> {
    return this.http.get<PagedInvoicesDto>(`${this.apiUrl}${API_ENDPOINTS.patients.invoices(patientId)}`, {
      params: { page, pageSize },
    });
  }

  // `GET /api/billing/invoices/{invoiceId}/pdf` is the SAME backend endpoint the clients
  // feature's `getInvoicePdf` already calls -- invoice PDF download is invoice-scoped, not
  // patient-scoped. Called directly here (rather than importing the clients feature) to keep
  // feature isolation intact; streams the PDF bytes directly (no JSON envelope), hence
  // `responseType: 'blob'`.
  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${API_ENDPOINTS.billing.invoicePdf(invoiceId)}`, { responseType: 'blob' });
  }

  // Level 3 of the consultation-attachments feature: re-resolves a fresh signed ProVet URL and
  // proxies the bytes (no JSON envelope), same `responseType: 'blob'` precedent as `getInvoicePdf`
  // above -- the level-2 `PatientConsultationDetailDto.Attachments` payload never carries the
  // signed URL itself, only the ids needed to ask for it again here.
  public downloadConsultationAttachment(
    patientId: string, consultationId: string, noteId: string, uploadId: string,
  ): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}${API_ENDPOINTS.patients.consultationAttachmentDownload(patientId, consultationId, noteId, uploadId)}`,
      { responseType: 'blob' },
    );
  }

  // `filters` is forwarded as `filters[key]=value` query params via Angular's `HttpParams`
  // object-key expansion (standard ASP.NET `[FromQuery] Dictionary<string, string>` model
  // binding on the backend) -- see ClientsController.GetClients/PatientsController.GetPatients.
  // `departmentId`, in contrast, is its OWN top-level query param (`[FromQuery] int?
  // departmentId`, separate from the `filters` dict) -- see `PatientListQuery.departmentId`'s
  // own doc-comment.
  private buildParams(
    base: Record<string, string | number>,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Record<string, string | number> {
    const withSort = sortBy ? { ...base, sortBy, sortDir: sortDir ?? 'asc' } : base;
    const withDepartment = departmentId === undefined ? withSort : { ...withSort, departmentId };
    if (!filters || Object.keys(filters).length === 0) return withDepartment;

    const filterParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      filterParams[`filters[${key}]`] = value;
    }
    return { ...withDepartment, ...filterParams };
  }
}
